import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Send, RotateCcw, ArrowUp } from 'lucide-react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';

// Test

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ChatInterface = () => {
  const { userId: routeUserId, type: routeType, hash: routeHash } = useParams();
  console.log('Route userId:', routeUserId);
  console.log('Route type:', routeType);
  console.log('Route hash:', routeHash);
  
  const [userId, setUserId] = useState(() => {
    const id = routeUserId || `User${Math.floor(Math.random() * 10000)}`;
    console.log('Using userId:', id);
    return id;
  });

  const [type, setType] = useState(() => {
    const typeValue = routeType || 'demo';
    console.log('Using type:', typeValue);
    return typeValue;
  });

  const [hash, setHash] = useState(() => {
    const hashValue = routeHash || 'demoHash';
    console.log('Using hash:', hashValue);
    return hashValue;
  });

  const [messages, setMessages] = useState([]);
  const [progress, setProgress] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isEnded, setIsEnded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [debugData, setDebugData] = useState(null);
  const [conversationEnded, setConversationEnded] = useState(false);
  const messagesEndRef = useRef(null);
  const userInputRef = useRef(null);
  const hasFetchedInitialQuestion = useRef(false);

  const shouldBeHalfWidth = useCallback((optionText) => {
    return optionText.length < 25;
  }, []);

  const handleSendAnswer = useCallback(async (event) => {
    if (event?.key === 'Enter' && event?.shiftKey) return;
    if (event?.key === 'Enter' && !event?.shiftKey) {
      event.preventDefault();
    }
    if ((!event || event.key === 'Enter') && (userInput || selectedOptions.length > 0)) {
      if (!currentQuestion) return;
      let answer;
      if (currentQuestion.multiple_choice?.length > 0) {
        answer = currentQuestion.multi_select?.toLowerCase() === "ja" ? selectedOptions.join(', ') : selectedOptions[0];
      } else {
        answer = userInput;
      }
  
      const userMessage = { role: 'user', content: answer };
      
      setUserInput('');
      setIsLoading(true);
  
      try {
        const response = await fetch(`${API_BASE_URL}/${userId}/next_question`, {
          method: 'POST',
          headers: {
            'Authorization': process.env.REACT_APP_API_AUTH,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({answer: answer}),
        });
        
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        console.log('Response from next_question:', data);
        
        if (Array.isArray(data.chat_history)) {
          console.log('Setting new chat history:', data.chat_history);
          setMessages(prevMessages => {
            const newMessages = [...data.chat_history];
            console.log('New messages array:', newMessages);
            return newMessages;
          });
        } else {
          console.warn('Received invalid chat_history:', data.chat_history);
        }
        
        if (typeof data.progress === 'number') {
          setProgress(data.progress);
        }
        
        setCurrentQuestion(data.payload);
        setSelectedOptions([]);
        
        if (data.completed) {
          setIsEnded(true);
        }

      } catch (error) {
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [currentQuestion, selectedOptions, userInput, userId]);

  const handleOptionChange = useCallback((option) => {
    setSelectedOptions(prevOptions =>
      currentQuestion?.multi_select?.toLowerCase() === "ja"
        ? prevOptions.includes(option)
          ? prevOptions.filter(item => item !== option)
          : [...prevOptions, option]
        : [option]
    );
  }, [currentQuestion]);

  const fetchQuestion = useCallback(async () => {
    if (hasFetchedInitialQuestion.current) return;
    setIsLoading(true);
    try {
      //const baseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, '');
      const endpoint = `${API_BASE_URL}/${type}/${userId}/${hash}/start_session`;
      
      console.log('Constructed endpoint:', endpoint);
      
      if (!userId) {
        throw new Error('No user ID provided');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': process.env.REACT_APP_API_AUTH,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        console.error('Response not OK:', response.status, response.statusText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      console.log('Chat History:', data.chat_history);
      
      if (typeof data.progress === 'number') {
        setProgress(data.progress);
      }

      setMessages(Array.isArray(data.chat_history) ? data.chat_history : []);
      setCurrentQuestion(data.payload);
      hasFetchedInitialQuestion.current = true;
    } catch (error) {
      console.error('Error fetching initial question:', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const renderOptions = useMemo(() => {
    if (!currentQuestion?.multiple_choice) return [];

    const fullWidthOptions = [];
    const halfWidthOptions = [];

    currentQuestion.multiple_choice.forEach((option) => {
      if (shouldBeHalfWidth(option)) {
        halfWidthOptions.push(option);
      } else {
        fullWidthOptions.push(option);
      }
    });

    return [...fullWidthOptions, ...halfWidthOptions].map((option, index) => (
      <div
        key={index}
        className={`px-1 mb-2 ${shouldBeHalfWidth(option) ? 'w-1/2' : 'w-full'}`}
      >
        <label
          className={`
            flex items-center space-x-2 bg-white p-2 rounded w-full cursor-pointer
            ${selectedOptions.includes(option) ? 'ring-2 ring-blue-500' : 'border border-black hover:bg-gray-50'}
            transition-all duration-200 ease-in-out
          `}
        >
          <input
            type={currentQuestion.multi_select?.toLowerCase() === "ja" ? "checkbox" : "radio"}
            checked={selectedOptions.includes(option)}
            onChange={() => handleOptionChange(option)}
            className="form-checkbox h-5 w-5 text-blue-600"
            name="options"
          />
          <span className="flex-1 text-sm">{option}</span>
        </label>
      </div>
    ));
  }, [currentQuestion, selectedOptions, shouldBeHalfWidth, handleOptionChange]);

  useEffect(() => {
    if (!hasFetchedInitialQuestion.current) {
      console.log("Fetching initial question...");
      fetchQuestion();
      hasFetchedInitialQuestion.current = true;
    }
  }, [fetchQuestion]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (currentQuestion && !currentQuestion.multiple_choice?.length) {
      userInputRef.current?.focus();
    }
  }, [currentQuestion]);

  useEffect(() => {
    console.log('Current messages:', messages);
  }, [messages]);

  useEffect(() => {
    console.log('Messages state updated:', messages);
  }, [messages]);

  return (
    <div className="flex flex-col h-screen max-w-md mx-auto p-4 bg-gray-100">
      <div className="w-full bg-white rounded-lg shadow p-4 mb-2 text-center">
        <h1 className="text-2xl font-bold">Didactia</h1>
        <p className="text-sm text-gray-600">{userId}</p>
        <p className="text-sm text-gray-500">{type}</p>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
        <div 
          className="bg-blue-500 h-2.5 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
      <div className="flex-grow overflow-y-auto mb-4 bg-white rounded-lg shadow p-4">
        {Array.isArray(messages) && messages.length > 0 ? (
          messages.map((message, index) => (
            <div 
              key={index} 
              className={`mb-2 ${message.role === 'user' ? 'text-right' : 'text-left'}`}
            >
              <span className={`inline-block rounded-lg p-2 ${
                message.role === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}>
                {message.content}
              </span>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-500">No messages yet</div>
        )}
        {isLoading && (
          <div className="text-center p-4 my-2 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {!isEnded && !conversationEnded && (
        <div className="space-y-2">
          {currentQuestion && currentQuestion.multiple_choice && currentQuestion.multiple_choice.length > 0 ? (
            <div className="space-y-2">
              {currentQuestion.multi_select?.toLowerCase() === "ja" && (
                <div className="italic text-gray-600">Meerdere antwoorden mogelijk</div>
              )}
              <div className="max-h-75 min-h-32 overflow-y-auto pr-2 border border-gray-200 rounded-lg p-2 bg-gray-50">
                <div className="flex flex-wrap -mx-1">
                  {renderOptions}
                </div>
              </div>
            </div>
          ) : (
            <input
              ref={userInputRef}
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleSendAnswer}
              placeholder="Type your answer..."
              className="w-full p-2 border rounded"
            />
          )}
          <div className="flex justify-between">
            <button 
              onClick={() => handleSendAnswer()}
              disabled={isLoading || (!userInput && selectedOptions.length === 0)}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentQuestion && currentQuestion.multiple_choice && currentQuestion.multiple_choice.length > 0 ? (
                <ArrowUp className="w-4 h-4 mr-2 inline" />
              ) : (
                <Send className="w-4 h-4 mr-2 inline" />
              )}
              Enter
            </button>
          </div>
        </div>
      )}

      {debugData && (
        <div className="overflow-x-auto bg-white rounded-lg shadow p-4 mt-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Answer</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(debugData).map(([number, qaObject], index) => {
                const [question, answer] = Object.entries(qaObject)[0];
                return (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{question}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{answer}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <div className="App bg-gray-100 min-h-screen">
        <Routes>
          <Route path="/:userId/:type/:hash" element={<ChatInterface />} />
          <Route path="/:userId/:type" element={<ChatInterface />} />
          <Route path="/:userId" element={<ChatInterface />} />
          <Route path="/" element={<ChatInterface />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
};

export default App;